'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from '@posthog/next';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { PLUS_POSTHOG_EVENTS, PLUS_PRICING_SECTION_ID } from '@/app/[locale]/plus/constants';
import AnchorLink from '@/components/AnchorLink';
import ViverseButton from '@/components/ViverseButton';
import { DEFAULT_VIVERSE_ORIGIN } from '@/constants/domain';
import { useAuthContext } from '@/context/AuthContext';
import heroBackground from '@/public/images/viverse-plus/hero-background.webp';
import heroBlurredOverlay from '@/public/images/viverse-plus/hero-blurred-overlay.png';
import ArrowRight from '@/public/images/viverse-plus/icon_arrow_right.svg';
import ViversePlus from '@/public/images/viverse-plus/icon_viverse_plus.svg';
export default function HeroBanner() {
  const $ = _c(58);
  const t = useTranslations();
  const {
    isGamePlus
  } = useAuthContext();
  const posthog = usePostHog();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Image src={heroBackground} alt="" fill={true} priority={true} sizes="100vw" className="-z-30 object-cover object-[center_top] opacity-80 lg:object-center" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="pointer-events-none absolute -bottom-12 left-1/2 -z-20 h-[589px] w-[1440px] -translate-x-1/2 overflow-hidden opacity-100"><Image src={heroBlurredOverlay} alt="" width={1920} height={1080} priority={true} className="absolute top-1/2 left-1/2 max-w-none -translate-x-1/2 -translate-y-1/2 -rotate-[63.86deg] mix-blend-screen blur-[52px] brightness-150 saturate-125" /></div>;
    t2 = <div aria-hidden={true} className="absolute inset-0 -z-10 bg-[linear-gradient(181.45deg,transparent_31.6%,#121216_91.75%),linear-gradient(270deg,transparent_0%,#121216_100%)]" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      x: -28
    };
    t4 = {
      opacity: 1,
      x: 0
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1]
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = t("plus.hero-banner.eyebrow");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <p className="text-vive-primary-40 text-xs font-extrabold tracking-[6.15px] uppercase">{t6}</p>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t) {
    t8 = t("plus.hero-banner.title");
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ViversePlus aria-hidden={true} className="h-auto w-full overflow-visible" />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8) {
    t10 = <h1 aria-label={t8} className="flex h-16 w-full max-w-[300px] items-center sm:max-w-[357px]">{t9}</h1>;
    $[13] = t8;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden={true} className="h-[3px] w-8 rounded-full bg-[linear-gradient(90deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0.2)_100%),linear-gradient(88.41deg,#53fff6_0.37%,#ff82fb_92.47%)]" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t7) {
    t12 = <div className="flex w-full flex-col items-start gap-2">{t7}{t10}{t11}</div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t) {
    t13 = t("plus.hero-banner.description");
    $[19] = t;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13) {
    t14 = <p className="text-vive-primary-30 text-base leading-6 tracking-[0.2px]">{t13}</p>;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t) {
    t15 = t("plus.hero-banner.price");
    $[23] = t;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15) {
    t16 = <p className="text-base leading-6 font-semibold tracking-[0.2px] text-white">{t15}</p>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== posthog) {
    t17 = () => posthog?.capture(PLUS_POSTHOG_EVENTS.VIEW_PLANS, {
      position: "top"
    });
    $[27] = posthog;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== isGamePlus || $[30] !== t) {
    t18 = isGamePlus ? t("plus.cta.manage-subscription") : t("plus.hero-banner.cta");
    $[29] = isGamePlus;
    $[30] = t;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArrowRight aria-hidden={true} className="size-7" />;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t18) {
    t20 = <ViverseButton size="medium" variant="colorful" className="min-h-12 px-6 text-base">{t18}{t19}</ViverseButton>;
    $[33] = t18;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t17 || $[36] !== t20) {
    t21 = <AnchorLink href={`#${PLUS_PRICING_SECTION_ID}`} onClick={t17}>{t20}</AnchorLink>;
    $[35] = t17;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t) {
    t22 = t("plus.hero-banner.annual-billing-hint");
    $[38] = t;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t22) {
    t23 = <p className="text-vive-primary-30 text-xs leading-4 tracking-[0.15px] italic">{t22}</p>;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t) {
    t24 = t("plus.hero-banner.disclaimer");
    $[42] = t;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t) {
    t25 = t("plus.hero-banner.terms");
    $[44] = t;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t25) {
    t26 = <a href={`${DEFAULT_VIVERSE_ORIGIN}/terms-of-use`} target="_blank" rel="noreferrer" className="text-vive-brand-40 hover:text-vive-brand-50 focus-visible:outline-vive-border-focus focus-visible:outline-2 focus-visible:outline-offset-2">{t25}</a>;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t24 || $[49] !== t26) {
    t27 = <p className="text-vive-primary-30 max-w-md pt-6 text-xs leading-4 tracking-[0.15px]">{t24}{" "}{t26}</p>;
    $[48] = t24;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t12 || $[52] !== t14 || $[53] !== t16 || $[54] !== t21 || $[55] !== t23 || $[56] !== t27) {
    t28 = <div className="relative isolate min-h-[100px] overflow-hidden px-6 py-20 text-white sm:min-h-[640px] sm:px-12 lg:px-[max(72px,calc((100vw-1296px)/2))] lg:py-32">{t0}{t1}{t2}<div className="relative mx-auto flex w-full max-w-[1128px] items-center"><motion.div className="flex w-full max-w-[636px] flex-col items-start gap-6 lg:w-[636px]" initial={t3} animate={t4} transition={t5}>{t12}{t14}{t16}{t21}{t23}{t27}</motion.div></div></div>;
    $[51] = t12;
    $[52] = t14;
    $[53] = t16;
    $[54] = t21;
    $[55] = t23;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  return t28;
}